import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, CloudOff, CheckCircle } from 'lucide-react';
import { useOffline } from '../hooks/useOffline';

const OfflineIndicator: React.FC = () => {
  const { isOnline, isOffline, wasOffline, syncWhenOnline } = useOffline();
  const [showNotification, setShowNotification] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (isOffline) {
      setShowNotification(true);
    } else if (wasOffline && isOnline) {
      // Show "back online" notification briefly
      setShowNotification(true);
      handleSync();
      
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isOnline, isOffline, wasOffline]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await syncWhenOnline();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDismiss = () => {
    setShowNotification(false);
  };

  if (!showNotification) {
    return null;
  }

  return (
    <div className="fixed top-16 left-4 right-4 z-50 animate-slide-up">
      {isOffline ? (
        // Offline notification
        <div className="bg-orange-100 border border-orange-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center space-x-3">
            <WifiOff className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-orange-800">
                You're offline
              </h3>
              <p className="text-xs text-orange-700 mt-1">
                Your progress is being saved locally and will sync when you're back online.
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="text-orange-600 hover:text-orange-800 p-1"
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
          <div className="mt-3 flex items-center space-x-2 text-xs text-orange-700">
            <CloudOff className="h-4 w-4" />
            <span>Working offline</span>
          </div>
        </div>
      ) : (
        // Back online notification
        <div className="bg-green-100 border border-green-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center space-x-3">
            <Wifi className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-green-800">
                Back online
              </h3>
              <p className="text-xs text-green-700 mt-1">
                {isSyncing 
                  ? 'Syncing your offline progress...' 
                  : 'Your offline progress has been synced successfully.'
                }
              </p>
            </div>
            {!isSyncing && (
              <CheckCircle className="h-5 w-5 text-green-600" />
            )}
          </div>
          {isSyncing && (
            <div className="mt-3">
              <div className="flex items-center space-x-2 text-xs text-green-700">
                <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                <span>Syncing changes...</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;